import React, { useState } from "react";
import { api } from "../shared/axiosInstance";
import CandidateDetails from "./CandidateDetails";
import Select from "react-select";
import selectStyles from '../shared/SelectedStyles';
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { relativeTime } from "../../utils/relativeTime";

const reviewStatusOptions = [
    { value: "unreviewed", label: "Unreviewed" },
    { value: "moveForward", label: "Move Forward" },
    { value: "rejected", label: "Rejected" }
];

const CandidateList = ({
                           candidates,
                           targets,
                           jobId,
                           totalMatches,
                           currentPage,
                           pageSize,
                           onPageChange,
                           onReviewStatusChange
                       }) => {
    const [expandedCandidateId, setExpandedCandidateId] = useState(null);

    const categoryColors = {
        "Perfect": "text-green-400",
        "Great": "text-blue-400",
        "Potential": "text-yellow-400",
        "SubOptimal": "text-red-400",
        "Sub-Optimal": "text-red-400"  // support legacy dash version just in case
    };

    const handleReviewStatusChange = async (candidateId, newStatus) => {
        try {
            await api.post(`/jobs/${jobId}/review-status`, {
                userId: candidateId,
                status: newStatus
            });

            // Optional: fire external callback if provided
            if (onReviewStatusChange) {
                onReviewStatusChange(candidateId, newStatus);
            }
        } catch (err) {
            console.error("❌ Failed to update review status", err);
        }
    };

    return (
        <div className="bg-gray-800 p-4 rounded-lg w-full">
            <h2 className="text-xl font-bold text-white mb-4">Matched Candidates</h2>

            {candidates.length === 0 ? (
                <div className="text-center text-gray-400 py-10">
                    <p className="text-xl font-semibold mb-2">No candidates match your filters.</p>
                    <p className="text-sm">Try adjusting your filters or clicking “Show All Matches”.</p>
                </div>
            ) : (
                <ul>
                    {candidates.map((match) => {
                        const matchedUser = match.matchedUser || {};
                        const email = matchedUser.email || "No email provided";
                        const linkedin = matchedUser.linkedinProfile;
                        const rawCategory = match.category || "Potential";
                        const category = rawCategory === "subOptimal" ? "SubOptimal" :
                            rawCategory.charAt(0).toUpperCase() + rawCategory.slice(1);

                        const hasApplied = matchedUser.appliedJobs?.some(id => id.toString() === jobId.toString());

                        return (
                            <li key={match._id} className={`p-3 border-b border-gray-700 ${match.ineligibleReasons?.length > 0 ? 'opacity-50' : ''}`}>
                                <div className="flex flex-wrap sm:flex-nowrap items-center gap-4 justify-between">
                                    <div className="flex items-center flex-wrap gap-4">
                                        <div>
                                        <p className={`text-lg font-semibold ${categoryColors[category]} m-0`}>
                                            {matchedUser.firstName || "Unknown"} {matchedUser.lastName || ""}
                                            <span className="ml-3 text-sm font-normal">
                                                {category === "SubOptimal" ? "(Applied – Not a Match)" : `(${category})`}
                                            </span>
                                            {hasApplied && (
                                                <span className="ml-2 text-green-400 text-sm">(Applied)</span>
                                            )}
                                        </p>
                                        {match.ineligibleReasons?.length > 0 && (
                                            <div className="mt-1">
                                                {match.ineligibleReasons.map(reason => (
                                                    <span
                                                        key={reason}
                                                        className="inline-block bg-gray-600 text-gray-300 text-xs px-2 py-1 rounded mr-1"
                                                    >
                                                        {reason}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {relativeTime(match.assessmentCompletedAt || matchedUser?.updatedAt) && (
                                            <p className="text-xs text-gray-400 mt-1">
                                                {relativeTime(match.assessmentCompletedAt || matchedUser?.updatedAt)}
                                            </p>
                                        )}
                                        </div>
                                        <div className="w-48">
                                            <Select
                                                value={reviewStatusOptions.find(opt => opt.value === match.reviewStatus)}
                                                options={reviewStatusOptions}
                                                onChange={(selectedOption) =>
                                                    handleReviewStatusChange(matchedUser._id, selectedOption.value)
                                                }
                                                styles={selectStyles}
                                                classNamePrefix="select"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <p className="text-gray-400 flex items-center gap-2">
                                    Similarity Score: <span className="font-bold">{(match.similarityScore * 100).toFixed(2)}%</span>
                                </p>


                                {linkedin ? (
                                    <p>
                                        <a href={linkedin.startsWith("http") ? linkedin : `https://${linkedin}`}
                                           className="text-blue-400 hover:underline"
                                           target="_blank" rel="noopener noreferrer">
                                            View LinkedIn Profile
                                        </a>
                                    </p>
                                ) : (
                                    <p className="text-gray-400">No LinkedIn profile</p>
                                )}

                                <p>
                                    <a href={`mailto:${email}`} className="text-blue-400 hover:underline">{email}</a>
                                </p>

                                <button
                                    onClick={() =>
                                        setExpandedCandidateId(expandedCandidateId === match._id ? null : match._id)
                                    }
                                    className="mt-2 text-blue-400 hover:text-orange"
                                >
                                    {expandedCandidateId === match._id ? "Hide Details ▲" : "Show Details ▼"}
                                </button>

                                {expandedCandidateId === match._id && (
                                    <div className="mt-4">
                                        <CandidateDetails
                                            candidateId={matchedUser._id}
                                            candidateName={`${matchedUser.firstName || "Unknown"} ${matchedUser.lastName || ""}`}
                                            target={targets?.[0]}
                                            targetName={`${targets?.[0]?.firstName || "Target"} ${targets?.[0]?.lastName || ""}`}
                                            assessmentCompletedAt={match.assessmentCompletedAt}
                                        />
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            {candidates.length > 0 && (
                <div className="flex justify-between items-center mt-4 text-gray-300">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded ${
                            currentPage === 1 ? "bg-gray-600 text-gray-400 cursor-not-allowed" : "bg-gray-700 hover:bg-gray-600"
                        }`}
                    >
                        Previous
                    </button>
                    <span>
    Page {currentPage} of {Math.ceil(totalMatches / pageSize)}
</span>

                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={candidates.length < pageSize}
                        className={`px-4 py-2 rounded ${
                            candidates.length < pageSize ? "bg-gray-600 text-gray-400 cursor-not-allowed" : "bg-gray-700 hover:bg-gray-600"
                        }`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default CandidateList;
